import { authEndpoints } from '../../utils/constants';

export type OnboardingStatus =
  | { status: 'verification_required'; expiresAt: string }
  | { status: 'unauthenticated' };

export type SendVerificationResult = {
  status: 'sent';
  phoneNumber: string;
};

export type VerifyOtpResult =
  | { status: 'verified' }
  | { status: 'incorrect'; attemptsRemaining: number };

type ErrorBody = {
  code?: string;
  error?: string | { code?: string; message?: string };
  message?: string;
};

export class PhoneOnboardingError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number, message?: string) {
    super(message || code);
    this.name = 'PhoneOnboardingError';
    this.code = code;
    this.status = status;
  }
}

export type PhoneOnboardingApi = {
  getStatus: () => Promise<OnboardingStatus>;
  sendCode: (phoneNumber: string) => Promise<SendVerificationResult>;
  verifyCode: (code: string) => Promise<VerifyOtpResult>;
  cancel: () => Promise<void>;
};

const getErrorDetails = (body: ErrorBody) => {
  if (typeof body.error === 'object') {
    return {
      code: body.error.code || body.code || 'INTERNAL_ERROR',
      message: body.error.message || body.message,
    };
  }

  return {
    code: body.code || body.error || 'INTERNAL_ERROR',
    message: body.message,
  };
};

const readBody = async (response: Response): Promise<Record<string, any>> => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const request = async (
  fetchImplementation: typeof fetch,
  url: string,
  init: RequestInit
) => {
  const response = await fetchImplementation(url, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...init.headers,
    },
  });
  const body = await readBody(response);

  if (!response.ok) {
    const { code, message } = getErrorDetails(body);
    throw new PhoneOnboardingError(code, response.status, message);
  }

  return body;
};

export const createPhoneOnboardingApi = (
  fetchImplementation: typeof fetch = fetch
): PhoneOnboardingApi => ({
  async getStatus() {
    const response = await fetchImplementation(authEndpoints.phoneStatus, {
      credentials: 'include',
      method: 'GET',
      headers: {
        'content-type': 'application/json',
      },
    });

    if (response.status === 401) {
      return { status: 'unauthenticated' };
    }

    const body = await readBody(response);
    if (!response.ok) {
      const { code, message } = getErrorDetails(body);
      throw new PhoneOnboardingError(code, response.status, message);
    }

    if (body.status !== 'verification_required') {
      return { status: 'unauthenticated' };
    }

    if (typeof body.expires_at !== 'string') {
      throw new PhoneOnboardingError('INTERNAL_ERROR', response.status);
    }

    return {
      status: 'verification_required',
      expiresAt: body.expires_at,
    };
  },

  async sendCode(phoneNumber) {
    const body = await request(
      fetchImplementation,
      authEndpoints.phoneSendVerification,
      {
        method: 'POST',
        body: JSON.stringify({ phone_number: phoneNumber }),
      }
    );

    if (body.status !== 'sent' || typeof body.phone_number !== 'string') {
      throw new PhoneOnboardingError('INTERNAL_ERROR', 200);
    }

    return {
      status: 'sent',
      phoneNumber: body.phone_number,
    };
  },

  async verifyCode(code) {
    const body = await request(
      fetchImplementation,
      authEndpoints.phoneVerifyOtp,
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      }
    );

    if (
      body.status === 'incorrect' &&
      typeof body.attempts_remaining === 'number'
    ) {
      return {
        status: 'incorrect',
        attemptsRemaining: body.attempts_remaining,
      };
    }

    if (body.status === 'verified') {
      return { status: 'verified' };
    }

    throw new PhoneOnboardingError('INTERNAL_ERROR', 200);
  },

  // Discards the server-side continuation and clears the onboarding cookie.
  // Deliberately never rejects: a cleanup failure must not strand the user on
  // the verification screen, which is the only thing they can still reach.
  async cancel() {
    try {
      await fetchImplementation(authEndpoints.phoneCancelOnboarding, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'content-type': 'application/json',
        },
      });
    } catch {
      // Best-effort; the continuation expires on its own TTL regardless.
    }
  },
});

export const phoneOnboardingApi = createPhoneOnboardingApi();
